import formatters from '@/utils/formatters';

const CHAVE_CULTURAS = 'mensagens.diagnosticoSocioambiental.culturas';

/**
 * Formatação da capacidade produtiva de UMA cultura, no formato que o back do Lyra devolve em
 * `produtividade.culturas[]` (ver ProdutividadeCulturaOutput.java). Campo ausente (nulo ou indefinido) vira `--`
 * (RN34 da spec GS-3925 v7), inclusive as sacas: as linhas legadas da tabela de inteligência gravadas pelo pipeline
 * não guardam a taxa, então t/ha e sc/ha podem faltar mesmo quando há capacidade (RN35).
 * **Zero é valor, não ausência** (RN34 v7, GS-4090): imóvel sem área plantada tem 0 gravado na tabela e a tela mostra
 * 0,00 ha · 0,0 t/ha · ≈ 0 sc/ha · 0,0 t · ≈ 0 sc. Por isso nada aqui decide "tem dado" por truthiness.
 * Números seguem o idioma da tela (`locale` = this.$i18n.locale): 3,9 em pt-br, 3.9 em en.
 *
 * Casas decimais (RN32 da spec v5), com o arredondamento do toLocaleString: área em ha com duas; t e t/ha com uma;
 * sc e sc/ha sem casas e precedidos de "≈" (94,50 t sai "94,5 t" e "≈ 1.575 sc").
 *
 * O Diagnóstico v2 exibe todas as culturas da última safra que o imóvel tem (RN29); a ordem vem do back.
 */

export const SEM_DADO = '--';

const CASAS_AREA = 2;
const CASAS_TONELADAS = 1;
const CASAS_SACAS = 0;
const APROXIMADO = '≈';

// Zero conta como dado; só nulo e indefinido são ausência.
const temValor = valor => valor !== null && valor !== undefined;

export function formatarArea(cultura, locale) {
    if (!cultura || !temValor(cultura.area)) return SEM_DADO;
    return `${formatters.formatNumber(cultura.area, CASAS_AREA, locale)} ${
        cultura.areaUnidadeMedida || 'ha'
    }`;
}

// Taxa por hectare (t/ha). A unidade só aparece se o back mandou.
export function formatarTaxa(cultura, locale) {
    if (!cultura || !temValor(cultura.produtividade)) return SEM_DADO;
    const unidade = (cultura.produtividadeUnidadeMedida || '').trim();
    return `${formatters.formatNumber(
        cultura.produtividade,
        CASAS_TONELADAS,
        locale
    )}${
        unidade ? ` ${unidade}` : ''
    }`;
}

// Produtividade em sacas por hectare (saca de 60 kg, calculada no back).
export function formatarTaxaSacas(cultura, locale) {
    const sacas =
        cultura && cultura.calculoSaca && cultura.calculoSaca.mediaRegional;
    return temValor(sacas)
        ? `${APROXIMADO} ${formatters.formatNumber(
              sacas,
              CASAS_SACAS,
              locale
          )} sc/ha`
        : SEM_DADO;
}

// Zero (imóvel sem área plantada, que o pipeline grava como 0) é valor e sai "0,0 t" (RN34 v7); só nulo ou
// indefinido viram `--`.
export function formatarCapacidade(cultura, locale) {
    if (!cultura || !temValor(cultura.capacidadeProdutiva)) {
        return SEM_DADO;
    }
    return `${formatters.formatNumber(
        cultura.capacidadeProdutiva,
        CASAS_TONELADAS,
        locale
    )} t`;
}

// Capacidade em sacas (saca de 60 kg, calculada no back), sem casas decimais.
export function formatarCapacidadeSacas(cultura, locale) {
    const sacas =
        cultura && cultura.calculoSaca && cultura.calculoSaca.produtividade;
    return temValor(sacas)
        ? `${APROXIMADO} ${formatters.formatNumber(
              sacas,
              CASAS_SACAS,
              locale
          )} sc`
        : SEM_DADO;
}

/**
 * Os cinco valores exibidos de uma cultura (RN31). Sem cultura (imóvel sem dado na tabela de inteligência),
 * todos saem `--` (RN34).
 */
export function indicadoresCapacidade(cultura, locale) {
    return {
        area: formatarArea(cultura, locale),
        taxa: formatarTaxa(cultura, locale),
        taxaSacas: formatarTaxaSacas(cultura, locale),
        capacidade: formatarCapacidade(cultura, locale),
        capacidadeSacas: formatarCapacidadeSacas(cultura, locale)
    };
}

/**
 * O back já manda o rótulo pronto, a descrição de core.season: "Safra 24/25" ou "Safrinha 25/25" (RN30: não existem
 * outros tipos). Se vier só o código ("24_25"), prefixa a palavra. Testar só "safra" deixava "Safrinha" de fora e
 * a tela mostrava "Safra Safrinha 25/25".
 *
 * @param {function} t traduz uma chave (`this.$t`)
 */
export function rotuloSafra(safra, t) {
    if (!safra) return null;
    return /\bsafr(a|inha)\b/i.test(safra)
        ? safra
        : `${t('mensagens.diagnosticoSocioambiental.safra')} ${safra}`;
}

/**
 * Cada cultura traz a sua última safra em `cultura.safra` (GS-4110), então soja pode estar em "Safra 25/26" e milho em
 * "Safra 23/24". Devolve true quando há 2+ safras DISTINTAS na lista; nulo e vazio não contam como safra, e a
 * comparação ignora caixa e espaços. É esse caso que faz a tela trocar o rótulo único (`produtividade.safra`) pelo de
 * cada cultura.
 */
export function safrasDiferem(culturas) {
    if (!Array.isArray(culturas)) return false;
    const safras = new Set();
    culturas.forEach(cultura => {
        const safra =
            cultura && cultura.safra
                ? String(cultura.safra).trim().replace(/\s+/g, ' ').toLowerCase()
                : '';
        if (safra) safras.add(safra);
    });
    return safras.size > 1;
}

/**
 * Nome traduzido da cultura; cultura sem tradução cadastrada aparece pelo próprio código.
 *
 * @param {function} t traduz uma chave (`this.$t`)
 * @param {function} te diz se a chave existe (`this.$te`)
 */
export function nomeCultura(codigo, t, te) {
    if (!codigo) return SEM_DADO;
    const chave = `${CHAVE_CULTURAS}.${String(codigo).toUpperCase()}`;
    return te(chave) ? t(chave) : codigo;
}
